import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogTitle, IconButton, Slide, AppBar, Toolbar,
  Box, Stack, Typography, Chip, TextField, MenuItem, Autocomplete,
  CircularProgress, Alert, InputAdornment, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon, Save as SaveIcon, CheckCircle as SavedIcon,
  ErrorOutline as ErrorIcon, HourglassEmpty as PendingIcon,
  Notes as RemarksIcon, PersonOff as PersonOffIcon,
} from '@mui/icons-material';
import API from '../../BaseURL';
import swal from '../../utils/swal';

// RemarksSheetDialog — class-teacher-only per-class report-card sheet.
// Mirrors the raw score sheet's UX (fullscreen dialog, autosave, per-row
// status pill) but for the four remark buckets plus attendance:
//   • Attitude       → dropdown backed by remarks catalogue "Attitude"
//   • Conduct        → dropdown backed by "Conduct"
//   • Class-teacher  → dropdown backed by "Teachers Remark"
//   • Headmaster     → dropdown backed by "Headmasters Remark"
//   • Attendance     → number (days the student came to school)
//
// Data is loaded once from /examination/remarks/sheet (which returns
// every student in the class roster with their existing remarks) and
// saved back to the same endpoint. Autosave is debounced 700ms per row.
// The backend rejects the call with 403 if the caller isn't the class
// teacher — so this dialog can safely be opened from any card the
// portal decides to render.

const Transition = (props) => <Slide direction="up" {...props} />;

const CATEGORIES = [
  { key: 'att', label: 'Attitude',         catalog: 'Attitude' },
  { key: 'con', label: 'Conduct',          catalog: 'Conduct' },
  { key: 't_r', label: 'Class Teacher',    catalog: 'Teachers Remark' },
  { key: 'h_r', label: 'Headmaster',       catalog: 'Headmasters Remark' },
];

export default function RemarksSheetDialog({ open, onClose, bucket }) {
  // bucket = { cla, term, acc_yer } — no `state` (remarks aren't exam-state scoped)
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [rows, setRows]         = useState([]);
  const [catalog, setCatalog]   = useState({ att: [], con: [], t_r: [], h_r: [] });
  const [nextClass, setNextClass] = useState('');   // '' if this is the top class
  const [status, setStatus]     = useState({}); // ind -> 'idle'|'pending'|'saving'|'saved'|'error'
  // Days the daily register was taken for this class this term. Zero
  // means the register holds nothing for this scope — worth saying out
  // loud, because otherwise a column of blank attendance boxes looks
  // like the feature is broken rather than like nobody has marked yet.
  const [daysMarked, setDaysMarked] = useState(0);

  const cla     = bucket?.cla || '';
  const term    = bucket?.term || '';
  const acc_yer = bucket?.acc_yer || '';

  // Promotion column only shows for the LAST term of the year. Ghana
  // convention is Term 3 = end of year = promotion decisions happen
  // here. Any other term hides the column entirely.
  const isPromotionTerm = String(term || '').toLowerCase() === 'term_3';

  // Timers keyed by student index — one debounce per row so a teacher
  // ripping through the sheet doesn't fire a save on every keystroke.
  const timers = useRef({});
  const currentRows = useRef([]);
  useEffect(() => { currentRows.current = rows; }, [rows]);

  const load = useCallback(async () => {
    if (!open || !cla || !term || !acc_yer) return;
    setLoading(true); setError('');
    try {
      // Fetch classes only when we need the promotion column so we
      // don't waste a round-trip in Term 1 / Term 2.
      const requests = [
        API.get('/examination/remarks/sheet', { params: { cla, term, acc_yer } }),
        API.get('/examination/remarks/catalog'),
      ];
      if (isPromotionTerm) {
        requests.push(API.get('/classes').catch(() => ({ data: [] })));
      }
      const [sheet, cat, classesRes] = await Promise.all(requests);
      const raw = Array.isArray(sheet.data) ? sheet.data : (sheet.data?.rows || []);
      // How many days the register was taken for this class this term —
      // the denominator behind every attendance figure below.
      setDaysMarked(Number(sheet.data?.days_marked) || 0);
      setRows(raw.map(r => ({
        ind:          r.ind || '',
        na:           r.na  || '',
        att:          r.att || '',
        con:          r.con || '',
        t_r:          r.t_r || '',
        h_r:          r.h_r || '',
        to_a:          r.to_a ?? '',
        // What the daily register actually recorded. Read-only context
        // for the editable box beside it — never folded into `to_a`, so
        // a teacher can see when their own number disagrees with the roll.
        register_present: r.register_present ?? null,
        register_marked:  r.register_marked  ?? null,
        // class_status stores 'PROMOTED' or 'REPEATED' (or empty).
        // Empty is treated as PROMOTED in the UI so the teacher only
        // has to click the ones they want to hold back.
        class_status: r.class_status || '',
      })));
      const items = cat.data?.items || [];
      const byCat = { att: [], con: [], t_r: [], h_r: [] };
      for (const it of items) {
        if (it.cat === 'Attitude')          byCat.att.push(it.name);
        else if (it.cat === 'Conduct')      byCat.con.push(it.name);
        else if (it.cat === 'Teachers Remark')   byCat.t_r.push(it.name);
        else if (it.cat === 'Headmasters Remark') byCat.h_r.push(it.name);
      }
      for (const k of Object.keys(byCat)) byCat[k].sort((a, b) => a.localeCompare(b));
      setCatalog(byCat);
      // Compute the next class in the active order — this is what
      // the promotion dropdown shows as the default choice.
      if (isPromotionTerm && classesRes) {
        const classes = (classesRes.data || [])
          .filter(c => (c.is_active ?? 1))
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)
            || String(a.class).localeCompare(String(b.class)));
        const idx = classes.findIndex(c => c.class === cla);
        setNextClass(idx >= 0 && idx < classes.length - 1 ? classes[idx + 1].class : '');
      } else {
        setNextClass('');
      }
      setStatus({});
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load remarks sheet');
    } finally { setLoading(false); }
  }, [open, cla, term, acc_yer, isPromotionTerm]);
  useEffect(() => { load(); }, [load]);

  // Per-row save. Sends the whole row (all four remarks + attendance)
  // so the backend doesn't need to know which field changed.
  const saveRow = useCallback(async (ind) => {
    const row = currentRows.current.find(r => r.ind === ind);
    if (!row) return;
    setStatus(s => ({ ...s, [ind]: 'saving' }));
    try {
      await API.post('/examination/remarks/sheet', {
        cla, term, acc_yer,
        rows: [{
          ind: row.ind,
          na:  row.na,
          att: row.att,
          con: row.con,
          t_r: row.t_r,
          h_r: row.h_r,
          to_a: row.to_a === '' ? null : Number(row.to_a) || 0,
          class_status: row.class_status || null,
        }],
      });
      setStatus(s => ({ ...s, [ind]: 'saved' }));
      // Fade the "saved" pill back to idle after a beat.
      setTimeout(() => setStatus(s => ({ ...s, [ind]: 'idle' })), 1200);
    } catch (e) {
      setStatus(s => ({ ...s, [ind]: 'error' }));
      swal.error(e.response?.data?.message || 'Save failed');
    }
  }, [cla, term, acc_yer]);

  const queueSave = useCallback((ind) => {
    setStatus(s => ({ ...s, [ind]: 'pending' }));
    if (timers.current[ind]) clearTimeout(timers.current[ind]);
    timers.current[ind] = setTimeout(() => saveRow(ind), 700);
  }, [saveRow]);

  // Flush any pending debounce before closing so the last edit isn't
  // silently lost.
  const flushAll = useCallback(async () => {
    const pending = Object.keys(timers.current);
    for (const k of pending) {
      if (timers.current[k]) {
        clearTimeout(timers.current[k]);
        delete timers.current[k];
        await saveRow(k);
      }
    }
  }, [saveRow]);

  const patchRow = (ind, patch) => {
    setRows(prev => prev.map(r => r.ind === ind ? { ...r, ...patch } : r));
    queueSave(ind);
  };

  const handleClose = async () => {
    await flushAll();
    onClose?.();
  };

  const totalRows = rows.length;
  const filled = useMemo(() => rows.filter(r =>
    r.att || r.con || r.t_r || r.h_r || (r.to_a !== '' && r.to_a !== null)
  ).length, [rows]);
  const pct = totalRows > 0 ? Math.round((filled / totalRows) * 100) : 0;

  return (
    <Dialog fullScreen open={open} onClose={handleClose}
      TransitionComponent={Transition}>
      <AppBar position="sticky" color="primary" elevation={0}
        sx={{ borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
        <Toolbar sx={{ gap: 1.5 }}>
          <RemarksIcon />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>
              Class Remarks — {cla}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', lineHeight: 1.2 }}>
              {(term || '').replace('_', ' ')} · {acc_yer} · Autosaves as you type
              {isPromotionTerm && (nextClass
                ? ` · End of year — default promotion → ${nextClass}`
                : ' · End of year — this is the top class (Completed)')}
            </Typography>
          </Box>
          <Chip label={`${filled} / ${totalRows} filled · ${pct}%`}
            color="secondary" sx={{ fontWeight: 800, color: '#fff' }} />
          <Tooltip title="Save all and close">
            <IconButton color="inherit" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      <DialogContent sx={{ p: { xs: 0.5, sm: 1.5 }, bgcolor: 'grey.50' }}>
        {loading && (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        )}
        {!loading && error && (
          <Alert severity="error" sx={{ m: 1 }}>{error}</Alert>
        )}
        {!loading && !error && rows.length === 0 && (
          <Alert severity="info" sx={{ m: 1 }} icon={<PersonOffIcon />}>
            No students on the roster for <b>{cla}</b> yet.
          </Alert>
        )}
        {/* Where the attendance column comes from. Stated once, above
            the sheet, rather than left for the teacher to infer from
            numbers that appeared on their own.

            A zero here is worth its own message: it means the register
            holds nothing for THIS class in THIS year and term, and the
            commonest cause is the examination year/term differing from
            the one the register stamps its days with — the two
            calendars are configured on different screens and do drift. */}
        {!loading && !error && rows.length > 0 && (
          daysMarked > 0 ? (
            <Alert severity="success" variant="outlined" sx={{ m: 1, py: 0.2 }}>
              Attendance is filled in from the <b>daily register</b> —
              {' '}<b>{daysMarked}</b> day{daysMarked === 1 ? '' : 's'} marked for {cla} this term.
              Each figure is still editable; the register's own count is shown under the box.
            </Alert>
          ) : (
            <Alert severity="info" variant="outlined" sx={{ m: 1, py: 0.2 }}>
              The daily register holds no marked days for <b>{cla}</b> in{' '}
              <b>{String(term).replace(/_/g, ' ')} · {acc_yer}</b>, so attendance has not been
              filled in. Either the roll has not been taken yet, or the register is recording
              against a different year and term than this sheet.
            </Alert>
          )
        )}
        {!loading && !error && rows.length > 0 && (
          <Box sx={{
            bgcolor: '#fff', borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
            overflow: 'auto', maxHeight: '100%',
          }}>
            <Box component="table" sx={{
              width: '100%', borderCollapse: 'separate', borderSpacing: 0,
              '& th': {
                position: 'sticky', top: 0, zIndex: 2, bgcolor: 'grey.100',
                fontWeight: 800, fontSize: 11, textTransform: 'uppercase',
                letterSpacing: 0.5, color: 'text.secondary',
                textAlign: 'left', p: '8px 10px',
                borderBottom: '2px solid', borderColor: 'divider',
              },
              '& td': {
                p: '4px 6px', fontSize: 12.5, verticalAlign: 'middle',
                borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.05)',
              },
              '& tbody tr:nth-of-type(even)': { bgcolor: 'grey.50' },
              '& tbody tr:hover': { bgcolor: 'primary.50' },
            }}>
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: 110 }}>Index</th>
                  <th style={{ minWidth: 180 }}>Name</th>
                  <th style={{ minWidth: 170 }}>Attitude</th>
                  <th style={{ minWidth: 170 }}>Conduct</th>
                  <th style={{ minWidth: 200 }}>Class Teacher</th>
                  <th style={{ minWidth: 200 }}>Headmaster</th>
                  <th style={{ width: 118 }}>Attendance</th>
                  {isPromotionTerm && <th style={{ minWidth: 200 }}>Promotion</th>}
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const st = status[r.ind] || 'idle';
                  return (
                    <tr key={r.ind}>
                      <td style={{ textAlign: 'center', color: '#888' }}>{i + 1}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{r.ind}</td>
                      <td style={{ fontWeight: 600 }}>{r.na}</td>
                      {CATEGORIES.map(c => (
                        <td key={c.key}>
                          <Autocomplete
                            size="small" freeSolo disableClearable
                            options={catalog[c.key] || []}
                            value={r[c.key] || ''}
                            onChange={(_, v) => patchRow(r.ind, { [c.key]: v || '' })}
                            onInputChange={(_, v, reason) => {
                              if (reason === 'input') patchRow(r.ind, { [c.key]: v });
                            }}
                            renderInput={(params) => (
                              <TextField {...params} size="small" variant="standard"
                                placeholder="—"
                                InputProps={{ ...params.InputProps, disableUnderline: true,
                                  sx: { fontSize: 12.5 } }} />
                            )} />
                        </td>
                      ))}
                      {/* Attendance — filled in from the daily register,
                          still editable. The register's own figure sits
                          under the box as plain text so the teacher can
                          see where the number came from, and can see when
                          they have typed something the roll disagrees
                          with. Overriding stays possible: a school that
                          started marking the register mid-term has a real
                          hand-counted figure for the weeks before it. */}
                      <td>
                        <TextField size="small" variant="standard" type="number"
                          value={r.to_a}
                          onChange={e => patchRow(r.ind, { to_a: e.target.value })}
                          InputProps={{ disableUnderline: true, sx: { fontSize: 12.5 },
                            endAdornment: <InputAdornment position="end" sx={{ mr: 0 }}>
                              <Typography variant="caption" color="text.disabled">days</Typography>
                            </InputAdornment> }}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }} />
                        {r.register_marked != null && (() => {
                          const typed = r.to_a === '' || r.to_a == null ? null : Number(r.to_a);
                          const differs = typed != null && typed !== r.register_present;
                          return (
                            <Tooltip title={differs
                              ? `The daily register recorded ${r.register_present} present out of `
                                + `${r.register_marked} day${r.register_marked === 1 ? '' : 's'} marked. `
                                + 'Your typed figure is used instead.'
                              : `From the daily register: ${r.register_present} present out of `
                                + `${r.register_marked} day${r.register_marked === 1 ? '' : 's'} marked.`}>
                              <Typography sx={{
                                display: 'block', textAlign: 'right', mt: 0.2,
                                fontSize: 10, fontWeight: 700, cursor: 'help',
                                color: differs ? 'warning.main' : 'text.disabled',
                              }}>
                                {differs ? '≠ ' : ''}register {r.register_present}/{r.register_marked}
                              </Typography>
                            </Tooltip>
                          );
                        })()}
                      </td>
                      {isPromotionTerm && (
                        <td>
                          {(() => {
                            // Blank class_status is treated as PROMOTED
                            // (the default). If there's no next class
                            // (top of the ladder), "Completed" replaces
                            // "Promoted → …" so the label stays truthful.
                            const isRepeated = r.class_status === 'REPEATED';
                            const promotedLabel = nextClass
                              ? `Promoted → ${nextClass}`
                              : 'Completed';
                            return (
                              <TextField select size="small" variant="standard"
                                value={isRepeated ? 'REPEATED' : 'PROMOTED'}
                                onChange={(e) => patchRow(r.ind, { class_status: e.target.value })}
                                InputProps={{ disableUnderline: true, sx: { fontSize: 12.5,
                                  fontWeight: 700,
                                  color: isRepeated ? 'error.main' : 'success.main' } }}
                                SelectProps={{ MenuProps: { PaperProps: { sx: { fontSize: 12.5 } } } }}
                                fullWidth>
                                <MenuItem value="PROMOTED">{promotedLabel}</MenuItem>
                                <MenuItem value="REPEATED">Repeated</MenuItem>
                              </TextField>
                            );
                          })()}
                        </td>
                      )}
                      <td style={{ textAlign: 'center' }}>
                        <StatusPill status={st} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatusPill({ status }) {
  if (status === 'pending') return <Chip size="small" icon={<PendingIcon sx={{ fontSize: 14 }} />} label="…" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} variant="outlined" />;
  if (status === 'saving')  return <Chip size="small" icon={<SaveIcon sx={{ fontSize: 14 }} />} label="Saving" color="warning" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
  if (status === 'saved')   return <Chip size="small" icon={<SavedIcon sx={{ fontSize: 14 }} />} label="Saved" color="success" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
  if (status === 'error')   return <Chip size="small" icon={<ErrorIcon sx={{ fontSize: 14 }} />} label="Error" color="error" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />;
  return null;
}
